import { clientModule } from "../../client/clientModuleRegistry";
import { distinct } from "../../common/utils/arrayUtilities";
import { getSelector } from "../../common/utils/generalUtils";

/**
 * This is a utility function that allows us to add specified fields to entities declared in the client module
 * 
 * This function takes the common defs and allows us to add cleint specified definitions to the entity builder
 */
export const addClientModuleFieldDefs = async (defs, clientConfig, t, entity, makeDef) => {
    const fieldDefs = (await clientModule(clientConfig))?.entityDefs?.(entity, defs, makeDef, t);
    return fieldDefs ?? defs;
}

const addPrefix = (prefix, field) => {
    if (field) {
        switch (typeof field) {
            case 'string':
                return (prefix ? prefix + '.' : '') + field;
            case 'object':
                return field.entity + '.' + field.field;
            default:
                throw new Error("Can't handl")
        }
    }
    return field;
}

/**
 * This function builds a function that can be used to declare fields of an entity.
 * 
 * The enity delcaration function takes the form of a function that can be used to specialize the returned columns
 * in the instantiation context.
 */
export const definitionFactory = (section, { prefix: globalPrefix, ...globalOptions } = {}) => {
    return function makeDef(type, title, { prefix: fieldPrefix, section: defSection, field: defField, ...defProps } = {}) {
        return function buildField(field, { prefix, ...props } = {}) {
            const p = prefix ?? fieldPrefix ?? globalPrefix;
            let f = defField !== undefined ? defField : field;
            if (typeof f == 'string') {
                f = addPrefix(p, f);
            }
            else if (f && Array.isArray(f)) {
                f = f.map(e => ({ ...e, src: addPrefix(p, e.src) }));
            }
            // Only processes fields if they have a length, preventing empty fields from being processed further
            else if (f && Object.keys(f).length > 0) {
                f = { ...f };
                Object.keys(f).forEach(e => f[e] = addPrefix(p, f[e]));
            }
            return { type, title, field: f, section: defSection ? section + ' - ' + defSection : section, ...globalOptions, ...defProps, ...props };
        }
    }
}

/**
 * This is a utility function to be used the the declaration function from a definitionFactory.
 * 
 * It takes a def dictionary and the deinition building function and processes a set of customfield
 * declarations to add the fields to the definition map.
 */
const processField = (defs, makeDef, field, type, label, sectionTitle, prefix, props) => {
    if (typeof field === 'string') {
        defs[addPrefix(prefix, field)] = makeDef(type, label, { 
            section: sectionTitle, 
            field: addPrefix(prefix, field), 
            ...props
        });
    } else if (typeof field === 'object') {
        const uuid = crypto.randomUUID();
        let field;
        if (!field || Object.keys(field).length === 0) {
            field = prefix;
        } else {
            const prefixedField = { ...field };
            Object.keys(prefixedField).forEach(f => (prefixedField[f] = addPrefix(prefix, prefixedField[f])));
            field = prefixedField
        }
        defs[uuid] = makeDef(type, typeof label === 'string' ? label : label?.default, {
            section: sectionTitle,
            field,
            uuid,
            ...props
        });
    } else {
        const uuid = crypto.randomUUID();
        defs[uuid] = makeDef(type, label, {
            section: sectionTitle,
            field: addPrefix(prefix, field),
            uuid,
            ...props
        });
    }
};
/**
 * Processes and adds field definitions from a form structure with multiple sections and rows.
 * This function handles nested form structures where each section can contain multiple rows of fields.
 * Used for forms like memberCustomFields where we have emergency contact forms, registration forms, etc.
 */
export const addCustomFormDefs = (defs, makeDef, customForm, prefix = 'customfields') => {
    customForm?.forEach(section => {
        section?.rows?.flat().forEach(({ field, type, label, text, ...props }) => {
            processField(defs, makeDef, field ?? null, type, label, section.title, prefix, props);
        });
    })
};

/** 
 * Processes and adds field definitions from a flat list of fields.
 * This function handles simpler form structures where fields are in a single array
 * without sections or rows.
 * Used for configuring custom fields like clientConfig.kiosk, settings panels, or other simple data entry forms.
 *
*/
export const addCustomFieldDefs = (defs, makeDef, fields, prefix = 'customfields') => {
    fields?.forEach(({ field, type, label, text, ...props }) => {
        processField(defs, makeDef, field ?? null, type, label, null, prefix, props);
    });
};

export const addDefaultExports = (defs, exportFieldList) => {
    exportFieldList?.forEach(fieldKey => {
        if (defs[fieldKey]) {
            defs[fieldKey].csvGroups = defs[fieldKey].csvGroups
                ? defs[fieldKey].csvGroups.concat(exportFieldList.title)
                : [exportFieldList.title];
        }
    });   
}

/**
 * This is a terminal factory used in building a set of entity definitions.
 * 
 * Once the functions above have built an object that maps a field to a definition builder this
 * factory takes care of applying those definition to a set of core columns. Any columns not selected
 * are returned as extra columns which could be selected later.
 */
export const fieldBuilderFactory = (defs, defEntity) => {
    return function fieldBuilder ({ columns, extraColumns }, options = {}) {
        return {
            columns: columns.map(({ entity, field, ...props }) => {
                if (!entity || entity !== defEntity) {
                    return { entity, field, ...props };
                } else {
                    if (!defs[field]) {
                        throw new Error(`Unknown field ${field} for entity ${defEntity}`);
                    }
                    return { ...defs[field](field, options),  ...props };
                }
            }),
            extraColumns: [...extraColumns, ...Object.keys(defs)
                .filter(field => columns.every(c => c.entity !== defEntity || c.field !== field))
                .map(field => {
                    return defs[field](field, options)
                })]
        };
    }
}

/**
 * The column builder is used to add report specific columns that are not from any entity
 * These are denoted in the core columns specification as entries that have only a field - ie they are NOT derrived from an entity
 * The column builder will process the column list like a fieldBuilder - any column entry that has a null / undefined entity and
 * which is not found, by field name, in the column definitions given to the builder is an error as these would otherwise escape
 * the builder process. This function is internal and only used in buildFields - it MUST NOT be exported
 * 
 * NOTE: entity needs to be null if passing through non-entity fields, so they are not confused with transformed fields
 */

const columnBuilder = colDefs => ({ columns, extraColumns }, options = {}) => {
    const colMap = { };
    colDefs.forEach(def => {
        if (typeof def.uuid === 'string') {
            colMap[def.uuid] = def;
        } else if (typeof def.field === 'string') {
            colMap[def.field] = def;
        } else if (Array.isArray(def.field)) {
            if (def.field.every(f => typeof f === 'string')) {
                // do nothing - value is the source of the value and so there are no columns everything would be from value
            } else {
                const srcField = def.field.find(f => f.dest === 'value')?.src;
                if (srcField) {
                    colMap[srcField] = def;
                }
            }
        } else if (def.field.value) {
            colMap[def.field.value] = def;
        }
    });
    return {
        columns: columns.map(entry => {
            if (!Object.hasOwn(entry, 'entity') || entry.entity !== null)  {
                return entry;
            } else {
                const column = colMap[entry.field];
                if (!column) {
                    throw new Error(`Unknown column ${entry.field} found with no entity and not in available colDefs`);
                }
                delete colMap[entry.field];
                return { ...column, ...options };
            }
        }),
        extraColumns: [ ...extraColumns, ...Object.values(colMap).map(col => ({ ...col, ...options })) ]
    }
}

/**
 * The entity registry handles the process of registring definition builder infrastructure against an entiy name.
 * The key contribution here are that the definition builders are setup lazily to minimize payload / chunk size.
 */
const createEntityRegistry = () => {
    const entityRegistry = { };
    const entityDefs = { };

    return {
        // used to register an entity and an import function which will load the entity builders
        register: (entity, load) => entityRegistry[entity] = load,
        // used to instantiate a set of defintions
        buildFields: async (context, fields, { entities = distinct(fields?.map(f => f.entity)), prefixes, extraColumns = [], configExports = [], ...options }) => {
            await Promise.all(entities.map(async entity => {
                if (entity && !entityDefs[entity]) {
                    const module = await (entityRegistry[entity]());
                    entityDefs[entity] = module.default;
                }
            }));

            let entityColumns = { columns: fields, extraColumns: [] };

            for (const entity of entities) {
                if (entity) {
                    const prefix = prefixes && Object.hasOwn(prefixes, entity) ? prefixes[entity] : entity;
                    const builder = await entityDefs[entity](context, entity);
                    entityColumns = builder(entityColumns, { ...options, prefix });
                } else {
                    entityColumns = columnBuilder(extraColumns)(entityColumns, options);
                }
            }

            configExports.forEach(({ name, columns }) => {
                columns.forEach(col => {
                    const { reactField, field, computation, title } = col;
                    const selector = getSelector({ ...col, field: reactField ?? field });
                    let f = reactField ?? field;
                    if (f) {
                        let fieldName = f;
                        if (prefixes) {
                            Object.entries(prefixes).forEach(([ entity, prefix ]) => {
                                if (fieldName.startsWith(entity + '.')) {
                                    if (prefix) {
                                        fieldName = prefix + fieldName.slice(entity.length);
                                    } else {
                                        fieldName = fieldName.slice(entity.length + 1);
                                    }
                                }
                            });
                        }
                        const fSelector = getSelector({ ...col, field: fieldName });
                        const existingColumn = entityColumns.columns.find(c => {
                            const colSelector = getSelector(c);
                            return colSelector == fSelector;
                        }) || entityColumns.extraColumns.find(c => {
                            const colSelector = getSelector(c);
                            return colSelector == fSelector;
                        });
                        if (existingColumn) {
                            if (!existingColumn.csvGroups) {
                                existingColumn.csvGroups = [];
                            }
                            existingColumn.csvGroups.push(name);
                        } else {
                            throw new Error("Unknown reactField " + reactField)
                        }
                    } else if (selector) {
                        const existingColumn = entityColumns.columns.find(c => {
                            const colSelector = getSelector(c);
                            return colSelector == selector;
                        }) || entityColumns.extraColumns.find(c => {
                            const colSelector = getSelector(c);
                            return colSelector == selector;
                        })
                        if (existingColumn) {
                            if (!existingColumn.csvGroups) {
                                existingColumn.csvGroups = [];
                            }
                            existingColumn.csvGroups.push(name);
                        } else {
                            entityColumns.extraColumns.push({ ...col, section: name, field: col.reactField ?? col.field, type: col.reactType ?? col.type ?? 'computed', csvGroups: [name] });
                        }
                    } else if (computation) {
                        const existingColumn = entityColumns.columns.find(c => c.computation === computation) || entityColumns.extraColumns.find(c => c.computation === computation);
                        if (existingColumn) {
                            if (!existingColumn.csvGroups) {
                                existingColumn.csvGroups = [];
                            }
                            existingColumn.csvGroups.push(name);
                        } else {
                            entityColumns.extraColumns.push({
                                type: 'computed',
                                title,
                                computation,
                                selector,
                                csvGroups: [name]
                            })
                        }
                    } else {
                        throw new Error('Unknown report column ' + JSON.stringify(col));
                    }
                })
            });
            
            return entityColumns;
        }
    }
}

export const entityRegistry = createEntityRegistry();